import styled from 'styled-components';

import Button from 'components/shared/Button';
import Input from 'components/shared/form/Input';
import StandardModal from 'components/shared/StandardModal';
import { Form, FormProvider, useForm } from 'context/form';
import { FormHelpers, FormMeta, FormValues } from 'context/form/types';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import Admission from 'models/reviews/Admission';
import { updateAdmission } from 'services/api/admissions';
import { useToastActions } from 'stores/toastStore';
import { BodySmall } from 'styles/typography';

type EditAdmissionFormValues = {
  patient: {
    name: string;
  };
  planType: string;
  referralSource: string;
};

type EditAdmissionFormMeta = {
  isSubmitting: boolean;
};

export type EditAdmissionPatientModalProps = {
  admission: Admission;
  onCancel: () => void;
};

export default function EditAdmissionPatientModal({ admission, onCancel }: EditAdmissionPatientModalProps) {
  const patient = admission.data.patient ?? {};
  const invalidateQueries = useInvalidateQueries();
  const { addToast } = useToastActions();

  const { mutate } = useMutateWithToast({
    mutationFn: updateAdmission,
  });

  const handleSubmit = (
    values: FormValues<EditAdmissionFormValues>,
    _meta: FormMeta<EditAdmissionFormMeta>,
    helpers: FormHelpers<EditAdmissionFormValues, EditAdmissionFormMeta>
  ) => {
    helpers.setMeta('isSubmitting', true);

    mutate(
      {
        id: admission.id,
        patient: { name: values.patient?.name ?? '' },
        planType: values.planType ?? '',
        referralSource: values.referralSource ?? '',
      },
      {
        onSuccess: () => {
          addToast({ text: 'Patient information was successfully updated!' });
          invalidateQueries({ admissions: true });
          onCancel();
        },
        onSettled: () => {
          helpers.setMeta('isSubmitting', false);
        },
      }
    );
  };

  return (
    <StandardModal onCancel={onCancel} title='Edit Patient Information'>
      <FormProvider<EditAdmissionFormValues, EditAdmissionFormMeta>
        onSubmit={handleSubmit}
        initialValues={{
          patient: { name: patient.name ?? '' },
          planType: admission.data.planType ?? '',
          referralSource: admission.data.referralSource ?? '',
        }}
        initialMeta={{ isSubmitting: false }}>
        <EditAdmissionPatientForm onCancel={onCancel} />
      </FormProvider>
    </StandardModal>
  );
}

function EditAdmissionPatientForm({ onCancel }: { onCancel: () => void }) {
  const { values, setValue, valid, dirty, meta } = useForm<EditAdmissionFormValues, EditAdmissionFormMeta>();

  return (
    <Form>
      <Content>
        <InputGroup>
          <BodySmall>Patient Name</BodySmall>
          <Input
            type='text'
            value={values.patient?.name ?? ''}
            onChange={(e) => setValue('patient.name', e.target.value)}
            placeholder='Enter patient name'
            disabled={meta.isSubmitting}
          />
        </InputGroup>

        <InputGroup>
          <BodySmall>Plan Type</BodySmall>
          <Input
            type='text'
            value={values.planType ?? ''}
            onChange={(e) => setValue('planType', e.target.value)}
            placeholder='Enter plan type'
            disabled={meta.isSubmitting}
          />
        </InputGroup>

        <InputGroup>
          <BodySmall>Referral Source</BodySmall>
          <Input
            type='text'
            value={values.referralSource ?? ''}
            onChange={(e) => setValue('referralSource', e.target.value)}
            placeholder='Enter referral source'
            disabled={meta.isSubmitting}
          />
        </InputGroup>
      </Content>
      <StandardModal.Actions>
        <Button variant='ghost' type='button' onClick={onCancel} disabled={meta.isSubmitting}>
          Cancel
        </Button>
        <Button type='submit' disabled={!valid || !dirty} loading={meta.isSubmitting}>
          Update
        </Button>
      </StandardModal.Actions>
    </Form>
  );
}

const Content = styled.div`
  margin: 16px 0 40px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
